(() => {
  if (customElements.get('soera-load-more')) return;
  class SoeraLoadMore extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section');
      this.grid = this.section.querySelector('[data-soera-product-grid]');
      const link = this.querySelector('.soera-load-more__button');
      if (link) {
        this.button = document.createElement('button');
        for (const attribute of link.attributes) {
          if (attribute.name !== 'href') this.button.setAttribute(attribute.name, attribute.value);
        }
        this.button.type = 'button';
        this.button.dataset.nextUrl = link.href;
        this.button.innerHTML = link.innerHTML;
        link.replaceWith(this.button);
      }
      this.status = this.querySelector('[role="status"]');
      this.urls = [];
      this.prefetched = null;
      this.onClick = this.load.bind(this);
      this.addEventListener('click', this.onClick);
      this.onPageHide = () => {
        if (this.urls.length) {
          try {
            sessionStorage.setItem(this.storageKey(), JSON.stringify({ urls: this.urls, y: scrollY, time: Date.now() }));
          } catch (_) {}
        }
      };
      window.addEventListener('pagehide', this.onPageHide);
      this.onPageShow = (event) => {
        if (event.persisted && this.urls.length) {
          try { sessionStorage.removeItem(this.storageKey()); } catch (_) {}
        }
      };
      window.addEventListener('pageshow', this.onPageShow);
      this.onFilterInteraction = (event) => {
        if (event.target.closest('#CollectionFilterForm, .link-dropdown__link, .filter-group__applied-item, .filter-group__clear-link, .pagination a')) this.controller?.abort();
      };
      this.section.addEventListener('click', this.onFilterInteraction, true);
      this.section.addEventListener('change', this.onFilterInteraction, true);
      // Fetch the next page while the button approaches the viewport, so the
      // tap usually swaps placeholders for real cards inside a few frames.
      if (this.button && 'IntersectionObserver' in window && !navigator.connection?.saveData) {
        this.prefetchObserver = new IntersectionObserver((entries) => {
          if (entries.some(entry => entry.isIntersecting)) {
            this.prefetchObserver.disconnect();
            this.prefetch();
          }
        }, { rootMargin: '0px 0px 900px 0px' });
        this.prefetchObserver.observe(this.button);
      }
      this.restore();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.prefetchObserver?.disconnect();
      this.removeEventListener('click', this.onClick);
      window.removeEventListener('pagehide', this.onPageHide);
      window.removeEventListener('pageshow', this.onPageShow);
      this.section.removeEventListener('click', this.onFilterInteraction, true);
      this.section.removeEventListener('change', this.onFilterInteraction, true);
    }
    storageKey() {
      const url = new URL(location.href);
      url.searchParams.delete('shpxid');
      url.searchParams.sort();
      return `soera-load-more:${url.pathname}${url.search}`;
    }
    requestFor(href) {
      const request = new URL(href, location.href);
      if (request.origin !== location.origin) return null;
      request.searchParams.set('section_id', this.dataset.sectionId);
      return request;
    }
    prefetch() {
      if (!this.button || this.loading || this.prefetched) return;
      const href = this.button.dataset.nextUrl;
      const request = this.requestFor(href);
      if (!request) return;
      const controller = new AbortController();
      const promise = fetch(request, { signal: controller.signal, priority: 'low' })
        .then(response => response.ok ? response.text() : Promise.reject(new Error(`HTTP ${response.status}`)))
        .catch(() => null);
      this.prefetched = { href, promise, controller };
    }
    async fetchPage(href, signal) {
      if (this.prefetched && this.prefetched.href === href) {
        const html = await this.prefetched.promise;
        this.prefetched = null;
        if (html) return html;
      }
      const request = this.requestFor(href);
      if (!request) throw new Error('Cross-origin page');
      const response = await fetch(request, { signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.text();
    }
    // Same-size stand-ins appended the moment the button is tapped: the
    // button, count and footer move once, inside the 500 ms input window, and
    // stay put when the real cards replace the stand-ins.
    placeholders(count) {
      const sample = this.grid.querySelector(':scope > product-block:last-of-type');
      if (!sample || count <= 0) return [];
      const height = sample.getBoundingClientRect().height;
      const made = [];
      for (let i = 0; i < count; i++) {
        const card = document.createElement('div');
        card.className = 'product-block soera-load-more__placeholder';
        card.setAttribute('aria-hidden', 'true');
        if (height > 0) card.style.height = `${height}px`;
        made.push(card);
      }
      this.grid.append(...made);
      return made;
    }
    async restore() {
      const savedKey = this.storageKey();
      let saved;
      try {
        saved = JSON.parse(sessionStorage.getItem(savedKey));
        sessionStorage.removeItem(savedKey);
      } catch (_) { return; }
      if (!saved?.urls?.length || Date.now() - saved.time > 1200000) return;
      let completed = true;
      for (const url of saved.urls) {
        if (!this.isConnected || !this.button || this.button.dataset.nextUrl !== url || !await this.append(url, true)) {
          completed = false;
          break;
        }
      }
      if (completed && this.isConnected && this.storageKey() === savedKey && !this.grid.closest('.collection-listing')?.classList.contains('ajax-loading')) {
        window.scrollTo({ top: saved.y, behavior: 'instant' });
      } else {
        try { sessionStorage.setItem(savedKey, JSON.stringify(saved)); } catch (_) {}
      }
    }
    async load(event) {
      if (!event.target.closest('.soera-load-more__button')) return;
      event.preventDefault();
      if (this.loading || !this.button) return;
      await this.append(this.button.dataset.nextUrl, false);
    }
    async append(href, restoring) {
      if (this.loading) return false;
      if (!this.requestFor(href)) return false;
      const collectionKey = this.storageKey();
      this.loading = true;
      this.controller = new AbortController();
      this.button.disabled = true;
      this.button.setAttribute('aria-disabled', 'true');
      this.button.setAttribute('aria-busy', 'true');
      this.button.querySelector('span').textContent = this.dataset.loadingLabel;
      this.status.textContent = '';
      let standIns = [];
      if (!restoring) {
        const shown = this.grid.querySelectorAll(':scope > product-block').length;
        const perPage = Number(this.dataset.perPage) || 24;
        standIns = this.placeholders(Math.min(perPage, Math.max(0, Number(this.dataset.total) - shown)));
      }
      try {
        const template = document.createElement('template');
        template.innerHTML = await this.fetchPage(href, this.controller.signal);
        const incomingGrid = template.content.querySelector('[data-soera-product-grid]');
        const incomingControl = template.content.querySelector('soera-load-more');
        if (!incomingGrid || !incomingControl) throw new Error('Missing collection content');
        if (!this.isConnected || !this.grid.isConnected || this.storageKey() !== collectionKey || this.grid.closest('.collection-listing')?.classList.contains('ajax-loading')) return false;
        const existing = new Set(Array.from(this.grid.querySelectorAll(':scope > product-block'), card => card.dataset.productId));
        const cards = Array.from(incomingGrid.children).filter(card => card.matches('product-block') && !existing.has(card.dataset.productId));
        if (!cards.length) throw new Error('No new products');
        cards.forEach(card => {
          card.querySelectorAll('img').forEach(image => {
            image.loading = 'lazy';
            image.setAttribute('fetchpriority', 'low');
          });
          card.querySelectorAll('[data-cc-animate]').forEach(element => element.removeAttribute('data-cc-animate'));
        });
        const y = scrollY;
        cards.forEach((card, index) => {
          const standIn = standIns[index];
          if (standIn && standIn.isConnected) standIn.replaceWith(card);
          else this.grid.appendChild(card);
        });
        standIns.slice(cards.length).forEach(standIn => standIn.remove());
        standIns = [];
        this.urls.push(href);
        const count = this.grid.querySelectorAll(':scope > product-block').length;
        this.querySelector('.soera-load-more__count').textContent = this.dataset.countLabel.replace('[count]', count).replace('[total]', this.dataset.total);
        const progress = this.querySelector('[role="progressbar"]');
        progress.setAttribute('aria-valuenow', count);
        progress.firstElementChild.style.width = `${Math.min(100, count / Number(this.dataset.total) * 100)}%`;
        const next = incomingControl.querySelector('.soera-load-more__button');
        if (next) {
          this.button.dataset.nextUrl = new URL(next.getAttribute('href'), location.href).href;
          this.prefetched = null;
          this.prefetch();
        } else {
          this.button.hidden = true;
          this.button = null;
        }
        const addedLabel = cards.length === 1 ? this.dataset.addedOneLabel : this.dataset.addedLabel;
        this.status.textContent = addedLabel.replace('[count]', cards.length);
        if (!restoring) {
          cards[0].querySelector('.product-link:not([tabindex="-1"])')?.focus({ preventScroll: true });
          window.scrollTo({ top: y, behavior: 'instant' });
        }
        this.dispatchEvent(new CustomEvent('soera:products-added', { bubbles: true, detail: { count: cards.length } }));
        return true;
      } catch (error) {
        standIns.forEach(standIn => standIn.remove());
        if (error.name !== 'AbortError' && this.isConnected) this.status.textContent = this.dataset.errorLabel;
        return false;
      } finally {
        this.loading = false;
        if (this.button) {
          this.button.disabled = false;
          this.button.removeAttribute('aria-disabled');
          this.button.removeAttribute('aria-busy');
          this.button.querySelector('span').textContent = this.dataset.buttonLabel;
        }
      }
    }
  }
  customElements.define('soera-load-more', SoeraLoadMore);
})();
